import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { formatDateFromUnix } from "../../permitComponents/permitComponentHelper";

const CustomersPackages = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [errorText, setErrorText] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/get-added-packages`,
        {
          currentSession,
          rowSize: pageSize,
          gridIndex: page,
          currentPath: window.location.pathname,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        const data = resp?.data?.data || {};
        const nextRows = Array.isArray(data?.rows)
          ? data.rows.map((row, index) => ({
              id: row?.task_id || `${page}-${index}`,
              ...row,
            }))
          : [];

        setRows(nextRows);
        setRowCount(Number(data?.totalCount || 0));
      } else {
        setRows([]);
        setRowCount(0);
        setErrorText(
          resp?.data?.message || "Failed to load customer packages.",
        );
      }
    } catch (error) {
      setRows([]);
      setRowCount(0);
      setErrorText(
        error?.response?.data?.message || "Failed to load customer packages.",
      );
    } finally {
      setLoading(false);
    }
  }, [currentSession, page, pageSize]);

  useEffect(() => {
    if (currentUser?.user?.role !== "admin") return;
    fetchRows();
  }, [currentUser?.user?.role, fetchRows]);

  const columns = useMemo(
    () => [
      {
        field: "project_name",
        headerName: "Package Name",
        flex: 1.4,
        minWidth: 220,
      },
      {
        field: "email",
        headerName: "Customer Email",
        flex: 1.2,
        minWidth: 220,
      },
      {
        field: "property_owner_name",
        headerName: "Owner",
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row?.property_owner_name || "N/A",
      },
      {
        field: "contractor_name",
        headerName: "Contractor",
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row?.contractor_name || "N/A",
      },
      {
        field: "county",
        headerName: "County",
        flex: 0.7,
        minWidth: 120,
      },
      {
        field: "city",
        headerName: "City",
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: "project_address",
        headerName: "Project Address",
        flex: 1.4,
        minWidth: 240,
        valueGetter: (_, row) => row?.project_address || "N/A",
      },
      {
        field: "permit_package_created",
        headerName: "Created",
        minWidth: 130,
        sortable: false,
        renderCell: (params) =>
          params?.row?.permit_package_created ? (
            <Chip label="Created" color="success" size="small" />
          ) : (
            <Chip label="Pending" color="default" size="small" />
          ),
      },
      {
        field: "created_at",
        headerName: "Created At",
        minWidth: 130,
        valueGetter: (_, row) =>
          row?.created_at ? formatDateFromUnix(row.created_at) : "N/A",
      },
    ],
    [],
  );

  if (currentUser?.user?.role !== "admin") {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">This page is available to admins only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography color="primary" variant={isMobile ? "h5" : "h4"}>
            Customers Packages
          </Typography>
          <Typography color="text.secondary" variant="body2" mt={0.5}>
            Read-only list of permit packages you created for customers.
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={1}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              mb={2}
            >
              <Typography variant="h6">Customer Permit Packages</Typography>
              <Chip
                label={`${rowCount} Total`}
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Box sx={{ height: 640, width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    {
                      outline: "none",
                    },
                }}
                slots={{
                  loadingOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ),
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={Boolean(errorText)}
        autoHideDuration={5000}
        onClose={() => setErrorText("")}
      >
        <Alert severity="error" onClose={() => setErrorText("")}>
          {errorText}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPackages;
